/**
 * AuditTab Component
 * Main container for MetricData audit functionality
 *
 * @module MetricDataDevTool/components/AuditTab
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import AdminButton from 'components/common/AdminButton';
import {
  PlayArrow as RunIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useUser } from 'hooks/useUser';
import AuditFilters from './AuditFilters';
import AuditResultsTable from './AuditResultsTable';
import { runAudit } from '../services/auditService';

/**
 * AuditTab component
 * Displays audit controls and results for MetricData quality analysis
 */
export default function AuditTab() {
  const { user } = useUser();

  // State
  const [selectedStores, setSelectedStores] = useState([]);
  const [stores, setStores] = useState([]); // Store the full store list for lookups
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [auditResults, setAuditResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Validate inputs before running audit
   * @returns {string|null} Error message or null if valid
   */
  const validateInputs = () => {
    if (!selectedStores || selectedStores.length === 0) {
      return 'Please select at least one store';
    }
    if (!dateRange.from || !dateRange.to) {
      return 'Please select a date range';
    }
    return null;
  };

  /**
   * Run audit query for all selected stores
   */
  const handleRunAudit = async () => {
    // Validate inputs
    const validationError = validateInputs();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);
    setAuditResults(null);

    try {
      const startDate = format(dateRange.from, 'yyyy-MM-dd');
      const endDate = format(dateRange.to, 'yyyy-MM-dd');

      console.log('🔍 Running audit...', {
        client: user.client,
        stores: selectedStores,
        startDate,
        endDate
      });

      // Run audit for ALL stores in a single query (more efficient with byClientDate index)
      let allStoreResults = [];

      try {
        const resultsByStore = await runAudit({
          clientId: user.client,
          storeIds: selectedStores,
          startDate,
          endDate
        });

        // Transform results to match expected format
        allStoreResults = selectedStores.map(storeId => ({
          storeId,
          results: resultsByStore[storeId] || [],
          success: true
        }));

        console.log('📊 [AuditTab] All store results:', allStoreResults);
      } catch (error) {
        console.error('❌ [AuditTab] Error running audit:', error);
        // Extract error message
        let errorMsg = 'Unknown error';
        if (error.message) {
          errorMsg = error.message;
        } else if (error.errors && error.errors[0]?.message) {
          errorMsg = error.errors[0].message;
        } else if (typeof error === 'string') {
          errorMsg = error;
        } else {
          errorMsg = JSON.stringify(error);
        }

        // Mark all stores as failed
        allStoreResults = selectedStores.map(storeId => ({
          storeId,
          results: [],
          success: false,
          error: errorMsg
        }));
      }

      // Check if any stores failed
      const failedStores = allStoreResults.filter(r => !r.success);
      if (failedStores.length > 0) {
        const errorMsg = `Failed to audit ${failedStores.length} store(s): ${failedStores.map(f => f.error).join(', ')}`;
        console.warn('⚠️ [AuditTab] Partial failure:', errorMsg);
        setError(errorMsg);
      } else {
        setError(null);
      }

      // Include all results (both successful and failed) for display
      setAuditResults(allStoreResults);
    } catch (err) {
      console.error('❌ Audit error:', err);
      setError(`Error running audit: ${err.message || err.toString() || 'Unknown error'}`);
      setAuditResults(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reset audit state
   */
  const handleReset = () => {
    setAuditResults(null);
    setError(null);
  };

  // Calculate summary stats across all stores
  const summaryStats = auditResults ? {
    totalStores: auditResults.length,
    totalMetrics: auditResults.reduce((sum, store) => sum + store.results.length, 0),
    discrepancies: auditResults.reduce((sum, store) =>
      sum + store.results.filter(r => !r.isValid).length, 0),
    uuidRecords: auditResults.reduce((sum, store) =>
      sum + store.results.reduce((s, r) => s + r.uuidCount, 0), 0),
    totalRecords: auditResults.reduce((sum, store) =>
      sum + store.results.reduce((s, r) => s + r.totalRecords, 0), 0)
  } : null;

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h5" gutterBottom>
          Audit Data Quality
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Verify record counts and identify legacy UUID-format metricIds
        </Typography>
      </Box>

      {/* Filters Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <AuditFilters
            selectedStores={selectedStores}
            onStoresChange={setSelectedStores}
            onStoresLoad={setStores}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            disabled={loading}
          />

          <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
            <AdminButton
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <RunIcon />}
              onClick={handleRunAudit}
              disabled={loading}
            >
              {loading ? 'Running Audit...' : 'Run Audit'}
            </AdminButton>

            {auditResults && (
              <AdminButton
                variant="outlined"
                onClick={handleReset}
                disabled={loading}
              >
                Reset
              </AdminButton>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Summary Stats */}
      {summaryStats && !loading && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Audit Summary
            </Typography>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Stores Audited
                </Typography>
                <Typography variant="h6">
                  {summaryStats.totalStores}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Metrics
                </Typography>
                <Typography variant="h6">
                  {summaryStats.totalMetrics}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Records
                </Typography>
                <Typography variant="h6">
                  {summaryStats.totalRecords}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Discrepancies
                </Typography>
                <Typography
                  variant="h6"
                  color={summaryStats.discrepancies > 0 ? 'error' : 'success.main'}
                >
                  {summaryStats.discrepancies}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  UUID Records
                </Typography>
                <Typography
                  variant="h6"
                  color={summaryStats.uuidRecords > 0 ? 'warning.main' : 'text.primary'}
                >
                  {summaryStats.uuidRecords}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Results Tables - One per store (expandable) */}
      {auditResults && !loading && auditResults.map((storeResult) => {
        const store = stores.find(s => s.id === storeResult.storeId);
        const storeName = store?.storeName || storeResult.storeId;
        const hasError = !storeResult.success;
        const hasNoResults = storeResult.success && storeResult.results.length === 0;
        const faultCount = storeResult.results.filter(r => !r.isValid).length;
        const failures = storeResult.results.filter(r => !r.isValid);
        const allResults = storeResult.results;
        const totalUuidCount = storeResult.results.reduce((sum, r) => sum + r.uuidCount, 0);

        return (
          <Accordion key={storeResult.storeId} defaultExpanded={faultCount > 0 || hasError || hasNoResults}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: faultCount > 0 || hasError ? '#fff3e0' : '#f5f5f5',
                '&:hover': {
                  backgroundColor: faultCount > 0 || hasError ? '#ffe0b2' : '#eeeeee'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', flexWrap: 'wrap' }}>
                <Typography variant="h6">
                  {storeName} ({storeResult.storeId})
                </Typography>
                {hasError ? (
                  <Chip
                    label="Error"
                    color="error"
                    size="small"
                  />
                ) : hasNoResults ? (
                  <Chip
                    label="No Results"
                    color="warning"
                    size="small"
                  />
                ) : (
                  <Chip
                    label={`${faultCount} ${faultCount === 1 ? 'Fault' : 'Faults'}`}
                    color={faultCount === 0 ? 'success' : 'error'}
                    size="small"
                  />
                )}
                {totalUuidCount > 0 && (
                  <Chip
                    label={`${totalUuidCount} UUID`}
                    color="warning"
                    size="small"
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {hasError ? (
                <Alert severity="error">
                  <Typography variant="body2">
                    <strong>Error:</strong> {storeResult.error}
                  </Typography>
                </Alert>
              ) : hasNoResults ? (
                <Alert severity="info">
                  <Typography variant="body2">
                    No metric data found for this store in the selected date range.
                  </Typography>
                </Alert>
              ) : (
                <>
                  {failures.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" color="error" gutterBottom sx={{ fontWeight: 600 }}>
                        Failures ({failures.length})
                      </Typography>
                      <AuditResultsTable results={failures} />
                    </Box>
                  )}
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    All Results ({allResults.length})
                  </Typography>
                  <AuditResultsTable results={allResults} />
                </>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Empty State */}
      {!auditResults && !loading && !error && (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Audit Results
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select a store and date range, then click "Run Audit" to begin
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
